import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Interface language. Citizen-facing pages are available in English and
 * Hindi; staff and admin workspaces are English for now.
 *
 * Hindi copy is written as people speak it — everyday Hindustani with the
 * English words people actually use (शिकायत, रिकॉर्ड, साइन इन, मोबाइल) —
 * not formal or machine-translated Hindi.
 *
 * Two ways to translate:
 *  - `tx("English", "हिंदी")` for page copy, kept next to the English;
 *  - `tl(label)` for shared labels (taxonomy, statuses, states) that come
 *    from @shared in English and are looked up in HI_LABELS below.
 */
export type Lang = "en" | "hi";

const dict = {
  nav_feed: { en: "Problems", hi: "समस्याएँ" },
  nav_record: { en: "Civic Record", hi: "जनता का रिकॉर्ड" },
  nav_promises: { en: "Promises", hi: "वादे" },
  nav_constituencies: { en: "Constituencies", hi: "चुनाव क्षेत्र" },
  nav_join: { en: "Join", hi: "जुड़िए" },
  nav_about: { en: "About", hi: "हमारे बारे में" },
  nav_report: { en: "Report an issue", hi: "शिकायत करें" },
  nav_signin: { en: "Sign in", hi: "साइन इन" },
  nav_account: { en: "My account", hi: "मेरा खाता" },
  nav_workspace: { en: "Workspace", hi: "वर्कस्पेस" },
  hero_kicker: { en: "Civic accountability infrastructure for India", hi: "भारत में जनता के प्रति जवाबदेही का मंच" },
  hero_title_1: { en: "The Republic", hi: "देश" },
  hero_title_2: { en: "remembers.", hi: "सब याद रखता है।" },
  hero_body: {
    en: "A permanent public memory of what was promised, what citizens lived through, and what was done about it — so that power, once delegated, stays answerable.",
    hi: "किसने क्या वादा किया, लोगों को किन मुश्किलों से गुज़रना पड़ा, और उस पर क्या हुआ — इन सबका एक पक्का सार्वजनिक रिकॉर्ड। ताकि जिन्हें हमने ताक़त सौंपी है, वो हमेशा हमें जवाब दें।",
  },
  cta_complaint: { en: "Register a complaint", hi: "शिकायत दर्ज करें" },
  cta_promise: { en: "Record a public promise", hi: "किसी नेता का वादा दर्ज करें" },
  freedom_title: { en: "Independence gave us a Republic. Memory will make it answer to us.", hi: "आज़ादी ने हमें लोकतंत्र दिया। अब याद रखना ही उसे हमारे प्रति जवाबदेह बनाएगा।" },
  join_title: { en: "This work needs founding citizens.", hi: "इस काम को शुरुआत करने वाले नागरिकों की ज़रूरत है।" },
} as const;

type Key = keyof typeof dict;

/** Hindi for labels that live in English in @shared (taxonomy, statuses, states, form options). */
const HI_LABELS: Record<string, string> = {
  // user activity counts (admin user detail)
  "Complaints submitted": "दर्ज की गई शिकायतें",
  "Complaints verified": "सत्यापित शिकायतें",
  "Promises submitted": "दर्ज किए गए वादे",
  "Promises verified": "सत्यापित वादे",
  "Verification decisions": "सत्यापन के फ़ैसले",
  "Evidence reviews": "सबूत की जाँचें",
  "Case actions": "मामलों पर कार्रवाइयाँ",
  "Assigned cases resolved": "सौंपे गए मामले जो सुलझे",
  "Tasks assigned": "सौंपे गए काम",
  "Tasks accepted": "स्वीकार किए गए काम",
  "Upheld violations": "सही पाए गए उल्लंघन",
  // retention actions
  "Review": "जाँच",
  "Archive": "संग्रह",
  "Purge": "मिटाना",
  "Anonymize": "गुमनाम करना",
  // Complaint categories and issues
  Roads: "सड़क",
  "Potholes or damaged road": "सड़क में गड्ढे या टूटी सड़क",
  "Unfinished road work": "सड़क का काम अधूरा पड़ा है",
  "Missing footpath": "फ़ुटपाथ नहीं है",
  "Broken streetlight": "स्ट्रीट लाइट ख़राब है",
  Encroachment: "सड़क पर कब्ज़ा (अतिक्रमण)",
  "Other road issue": "सड़क की कोई और समस्या",
  "Water supply": "पानी की सप्लाई",
  "No water supply": "पानी नहीं आ रहा",
  "Irregular supply": "पानी कभी आता है, कभी नहीं",
  "Contaminated water": "गंदा पानी आ रहा है",
  "Leaking pipeline": "पाइपलाइन लीक हो रही है",
  "Low pressure": "पानी का प्रेशर बहुत कम है",
  "Other water issue": "पानी की कोई और समस्या",
  Electricity: "बिजली",
  "Frequent outages": "बार-बार बिजली जाती है",
  "Dangerous wiring or pole": "खुले तार या ख़तरनाक खंभा",
  "Transformer fault": "ट्रांसफ़ॉर्मर ख़राब है",
  "Incorrect billing": "बिजली का बिल ग़लत आया",
  "New connection delayed": "नया कनेक्शन मिलने में देरी",
  "Other electricity issue": "बिजली की कोई और समस्या",
  "Drainage & sewerage": "नाली और सीवर",
  "Blocked drain": "नाली जाम है",
  "Overflowing sewer": "सीवर उफन रहा है",
  Waterlogging: "जलभराव — पानी भरा रहता है",
  "Open manhole": "मैनहोल खुला पड़ा है",
  "Other drainage issue": "नाली-सीवर की कोई और समस्या",
  "Garbage & sanitation": "कूड़ा और सफ़ाई",
  "Garbage not collected": "कूड़ा नहीं उठाया जा रहा",
  "Illegal dumping": "ग़लत जगह कूड़ा फेंका जा रहा है",
  "Public toilet not maintained": "सार्वजनिक शौचालय की हालत ख़राब है",
  "Burning of waste": "कूड़ा जलाया जा रहा है",
  "Other sanitation issue": "सफ़ाई की कोई और समस्या",
  "Schools & education": "स्कूल और पढ़ाई",
  "Teacher absence": "टीचर स्कूल नहीं आते",
  "Unsafe building": "स्कूल की इमारत ख़तरनाक है",
  "Mid-day meal problem": "मिड-डे मील में गड़बड़ी",
  "Lack of facilities": "ज़रूरी सुविधाएँ नहीं हैं",
  "Admission denied": "दाख़िला नहीं दिया गया",
  "Other school issue": "स्कूल की कोई और समस्या",
  "Hospitals & public health": "अस्पताल और सेहत",
  "Doctor or staff absence": "डॉक्टर या स्टाफ़ नहीं मिलते",
  "Medicines unavailable": "दवाइयाँ नहीं मिल रहीं",
  "Denial of treatment": "इलाज करने से मना कर दिया",
  "Unhygienic facility": "अस्पताल में गंदगी है",
  "Ambulance delay": "एम्बुलेंस देर से आई",
  "Other health issue": "सेहत से जुड़ी कोई और समस्या",
  "Public transport": "बस, ट्रेन और सरकारी परिवहन",
  "Irregular service": "बस या गाड़ी समय पर नहीं आती",
  "Unsafe vehicle": "गाड़ी की हालत असुरक्षित है",
  Overcharging: "ज़्यादा किराया वसूला गया",
  "Bus stop / station condition": "बस स्टॉप या स्टेशन की हालत ख़राब",
  "Other transport issue": "परिवहन की कोई और समस्या",
  "Government schemes": "सरकारी योजनाएँ",
  "Benefit not received": "योजना का फ़ायदा नहीं मिला",
  "Wrongful exclusion": "ग़लत तरीक़े से नाम काट दिया",
  "Application not processed": "आवेदन पर कोई कार्रवाई नहीं हुई",
  "Asked for documents repeatedly": "बार-बार काग़ज़ माँगे जा रहे हैं",
  "Other scheme issue": "योजना से जुड़ी कोई और समस्या",
  "Certificates, records & services": "प्रमाण पत्र, रिकॉर्ड और सरकारी सेवाएँ",
  "Certificate delayed": "प्रमाण पत्र मिलने में देरी",
  "Property mutation / registration": "ज़मीन-जायदाद का दाख़िल-ख़ारिज या रजिस्ट्री",
  "Ration card": "राशन कार्ड",
  "Name or detail error in records": "रिकॉर्ड में नाम या जानकारी ग़लत है",
  "Other service issue": "किसी और सेवा की समस्या",
  "Other public service": "कोई और सरकारी सेवा",
  Other: "कुछ और",

  // Conduct allegations
  Corruption: "भ्रष्टाचार",
  Bribery: "रिश्वत",
  "Misconduct / misbehaviour": "बदसलूकी या ग़लत बर्ताव",
  Harassment: "परेशान करना (उत्पीड़न)",
  "Abuse of authority": "पद का ग़लत इस्तेमाल",
  Negligence: "लापरवाही",
  "Unreasonable delay": "बेवजह देरी",
  "Refusal of duty": "काम करने से मना करना",
  "Dereliction of duty": "ड्यूटी में कोताही",

  // Complaint status and verification
  Submitted: "दर्ज हो गई",
  Acknowledged: "हमें मिल गई है",
  "Under review": "जाँच चल रही है",
  Assigned: "ज़िम्मेदारी सौंप दी गई",
  "In progress": "काम चल रहा है",
  Escalated: "ऊपर भेजी गई",
  Resolved: "सुलझ गई",
  Closed: "बंद",
  Reopened: "फिर से खोली गई",
  "Withdrawn by complainant": "शिकायत करने वाले ने वापस ले ली",
  "Not yet verified": "अभी जाँच नहीं हुई",
  "Evidence reviewed": "सबूत देखे गए",
  "Verified by Seva Sangrah": "सेवा संग्रह ने पुष्टि की",
  Disputed: "इस पर विवाद है",
  "Not substantiated": "साबित नहीं हुआ",
  "Verification decision": "जाँच का फ़ैसला",
  "Evidence review": "सबूतों की जाँच",
  "Investigation finding": "जाँच का नतीजा",
  "Official response": "अधिकारी का जवाब",
  "Representative response": "जनप्रतिनिधि का जवाब",
  "Final outcome": "आख़िरी नतीजा",
  "Problem resolved": "समस्या सुलझ गई",
  "Partially resolved": "कुछ हद तक सुलझी",
  "Administrative action taken": "प्रशासन ने कार्रवाई की",
  "Referred to competent authority": "सही विभाग को भेज दी गई",
  "No action by authority": "अधिकारियों ने कुछ नहीं किया",
  "Insufficient evidence": "सबूत काफ़ी नहीं थे",
  "Duplicate of another record": "यही मामला पहले से दर्ज है",
  "Outside Seva Sangrah's scope (private dispute)": "सेवा संग्रह के दायरे से बाहर (निजी झगड़ा)",
  "Dismissed by competent authority": "संबंधित अधिकारी ने ख़ारिज कर दी",

  // Promises
  "Roads & transport": "सड़क और परिवहन",
  Water: "पानी",
  Health: "सेहत",
  Education: "पढ़ाई-लिखाई",
  Sanitation: "सफ़ाई",
  Housing: "मकान",
  Employment: "रोज़गार",
  Agriculture: "खेती-किसानी",
  "Safety & policing": "सुरक्षा और पुलिस",
  Environment: "पर्यावरण",
  "Welfare schemes": "कल्याण योजनाएँ",
  "Governance & transparency": "सरकारी कामकाज और पारदर्शिता",
  "Manifesto commitment": "घोषणापत्र का वादा",
  "Public speech or rally": "भाषण या रैली में किया वादा",
  "Official announcement": "सरकारी घोषणा",
  "Budget commitment": "बजट में किया वादा",
  "Other public commitment": "कोई और सार्वजनिक वादा",
  MP: "सांसद (MP)",
  MLA: "विधायक (MLA)",
  "Former Incumbent": "पूर्व जनप्रतिनिधि",
  Candidate: "उम्मीदवार",
  "Government Department": "सरकारी विभाग",
  "Public Institution": "सरकारी संस्था",
  Community: "मोहल्ला या समुदाय",
  Ward: "वार्ड",
  City: "शहर",
  Constituency: "चुनाव क्षेत्र",
  District: "ज़िला",
  State: "राज्य",
  National: "पूरा देश",
  Announced: "घोषणा हुई",
  Fulfilled: "पूरा हुआ",
  "Partially fulfilled": "आधा-अधूरा पूरा हुआ",
  Unfulfilled: "पूरा नहीं हुआ",
  Broken: "वादा तोड़ा गया",

  // States and union territories
  "Andaman and Nicobar Islands": "अंडमान और निकोबार द्वीप समूह",
  "Andhra Pradesh": "आंध्र प्रदेश",
  "Arunachal Pradesh": "अरुणाचल प्रदेश",
  Assam: "असम",
  Bihar: "बिहार",
  Chandigarh: "चंडीगढ़",
  Chhattisgarh: "छत्तीसगढ़",
  "Dadra and Nagar Haveli and Daman and Diu": "दादरा और नगर हवेली और दमन और दीव",
  Delhi: "दिल्ली",
  Goa: "गोवा",
  Gujarat: "गुजरात",
  Haryana: "हरियाणा",
  "Himachal Pradesh": "हिमाचल प्रदेश",
  "Jammu and Kashmir": "जम्मू और कश्मीर",
  Jharkhand: "झारखंड",
  Karnataka: "कर्नाटक",
  Kerala: "केरल",
  Ladakh: "लद्दाख",
  Lakshadweep: "लक्षद्वीप",
  "Madhya Pradesh": "मध्य प्रदेश",
  Maharashtra: "महाराष्ट्र",
  Manipur: "मणिपुर",
  Meghalaya: "मेघालय",
  Mizoram: "मिज़ोरम",
  Nagaland: "नागालैंड",
  Odisha: "ओडिशा",
  Puducherry: "पुदुचेरी",
  Punjab: "पंजाब",
  Rajasthan: "राजस्थान",
  Sikkim: "सिक्किम",
  "Tamil Nadu": "तमिलनाडु",
  Telangana: "तेलंगाना",
  Tripura: "त्रिपुरा",
  "Uttar Pradesh": "उत्तर प्रदेश",
  Uttarakhand: "उत्तराखंड",
  "West Bengal": "पश्चिम बंगाल",

  // Join forms
  Awareness: "जागरूकता",
  "Help citizens understand Seva Sangrah and participate.": "लोगों को सेवा संग्रह के बारे में बताइए और उन्हें जोड़िए।",
  Verification: "जाँच-पड़ताल",
  "Help verify information, complaints and public records.": "जानकारी, शिकायतों और सरकारी रिकॉर्ड की जाँच में मदद कीजिए।",
  "Leadership Identification": "अच्छे लीडर की पहचान",
  "Help identify citizens and public servants who demonstrate leadership.": "ऐसे नागरिकों और अधिकारियों को पहचानने में मदद कीजिए जो सच में आगे बढ़कर काम करते हैं।",
  "Institutional Memory": "देश की याददाश्त",
  "Help preserve what the Republic should remember and learn from.": "जो बातें देश को याद रखनी चाहिए और जिनसे सीखना चाहिए, उन्हें सँभालने में मदद कीजिए।",
  "Full-time": "पूरा समय",
  "Part-time": "कुछ समय",
  Weekends: "सिर्फ़ छुट्टी वाले दिन",
  NGO: "NGO",
  "Civil Society Organisation": "सामाजिक संगठन",
  "Community Organisation": "सामुदायिक संगठन",
  "Research Institution": "रिसर्च संस्थान",
  "Community networks": "लोगों से जुड़ाव और नेटवर्क",
  "Civic awareness": "नागरिक जागरूकता",
  Research: "रिसर्च",
  "Field implementation": "ज़मीनी स्तर पर काम",
  "Data / technology": "डेटा या टेक्नोलॉजी",
  "Legal expertise": "क़ानूनी जानकारी",
  "Public policy expertise": "सरकारी नीतियों की समझ",
  Training: "ट्रेनिंग",
  Communications: "प्रचार और संवाद",
  "Public policy": "सरकारी नीतियाँ",
  "Law and constitutional affairs": "क़ानून और संविधान",
  "Research and academia": "रिसर्च और पढ़ाई-लिखाई",
  "Technology and data": "टेक्नोलॉजी और डेटा",
  Governance: "शासन-प्रशासन",
  "Design and user experience": "डिज़ाइन",
  "Training and capacity building": "ट्रेनिंग और सिखाना",
  "Media and storytelling": "मीडिया और कहानी कहना",
  Occasionally: "कभी-कभी",
  Monthly: "महीने में एक बार",
  Regularly: "नियमित रूप से",
  "On specific projects": "किसी ख़ास प्रोजेक्ट पर",
  "Explore a partnership": "साथ मिलकर काम करने के बारे में बात करनी है",
  "Discuss leadership / institutional collaboration": "नेतृत्व या संस्था के स्तर पर सहयोग",
  "Explore research or policy collaboration": "रिसर्च या नीति पर साथ काम",
  "Explore technology or implementation": "टेक्नोलॉजी या ज़मीनी काम में साथ",
  "Something else": "कुछ और",

  // Roles (server ROLE_LABELS) and actor labels on timelines (ROLE_LABEL_SHORT)
  "Super Admin": "सुपर एडमिन",
  Admin: "एडमिन",
  "Module Admin": "मॉड्यूल एडमिन",
  Citizen: "नागरिक",
  Volunteer: "वॉलंटियर",
  "Fellow / Leader": "फ़ेलो / लीडर",
  "Elected Representative": "चुने हुए जनप्रतिनिधि",
  "Representative Associate": "जनप्रतिनिधि के सहयोगी",
  "Political Party": "राजनीतिक पार्टी",
  "NGO / Civil Society": "NGO / सामाजिक संगठन",
  "Investigating Authority": "जाँच एजेंसी",
  Auditor: "ऑडिटर",
  "Seva Sangrah administration": "सेवा संग्रह प्रशासन",
  "Seva Sangrah Fellow": "सेवा संग्रह फ़ेलो",
  "Seva Sangrah volunteer": "सेवा संग्रह वॉलंटियर",
  "Elected representative": "चुने हुए जनप्रतिनिधि",
  "Representative's office": "जनप्रतिनिधि का दफ़्तर",
  "Political party": "राजनीतिक पार्टी",
  "Civil society partner": "साथी सामाजिक संगठन",
  "Investigating authority": "जाँच एजेंसी",
  Complainant: "शिकायत करने वाले",
  System: "सिस्टम",

  // Emergency numbers and reports
  "National emergency": "आपातकालीन नंबर",
  Police: "पुलिस",
  Fire: "फ़ायर ब्रिगेड",
  Ambulance: "एम्बुलेंस",
  Childline: "चाइल्डलाइन",
  "Women helpline": "महिला हेल्पलाइन",
  Spam: "स्पैम",
  "Fake or misleading": "झूठा या गुमराह करने वाला",
  Duplicate: "दोहराया गया",
  Abusive: "गाली-गलौज या अपमानजनक",
};

// The current language for plain helpers (dates, status labels in lib/format)
// that are called during render but are not hooks. Set by LangProvider as it
// renders, so it is current for everything rendered beneath it.
let currentLang: Lang = "en";
export const getLang = () => currentLang;
/** A shared English label in the current language, for non-hook code. */
export const labelIn = (label: string) => (currentLang === "hi" && HI_LABELS[label]) || label;

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (k: Key) => string;
  /** Inline copy: English and its Hindi side by side. */
  tx: (en: string, hi: string) => string;
  /** A shared English label in the current language. */
  tl: (label: string | null | undefined) => string;
};

const LangCtx = createContext<Ctx>({
  lang: "en",
  setLang: () => undefined,
  t: (k) => dict[k].en,
  tx: (en) => en,
  tl: (label) => label ?? "",
});

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    try {
      return (localStorage.getItem("seva.lang") as Lang) || "en";
    } catch {
      return "en";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("seva.lang", lang);
    } catch {
      /* private mode */
    }
    document.documentElement.lang = lang === "hi" ? "hi" : "en";
  }, [lang]);
  currentLang = lang;
  const t = useCallback((k: Key) => dict[k][lang], [lang]);
  const tx = useCallback((en: string, hi: string) => (lang === "hi" ? hi : en), [lang]);
  const tl = useCallback((label: string | null | undefined) => (label ? (lang === "hi" && HI_LABELS[label]) || label : ""), [lang]);
  const value = useMemo(() => ({ lang, setLang, t, tx, tl }), [lang, t, tx, tl]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
